import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { products } from '../mockData';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { ShoppingCart } from 'lucide-react';

const Products = () => {
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const { addToCart } = useCart();
  const { t, getProductName, getCategoryName, getDescription, formatLocalPrice, language, isRTL } = useLanguage();

  const categories = [
    'All', 
    'Beef Meat', 
    'Elite Beef Steaks',
    'Lamb Meat',
    'Homemade Products',
    'Smoked'
  ];

  // Read category from URL on mount
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam && categories.includes(categoryParam)) {
      setSelectedCategory(categoryParam);
    }
  }, [searchParams]);

  // Filter logic: Beef Meat includes Elite Beef Steaks products
  const filteredProducts = selectedCategory === 'All' 
    ? products 
    : selectedCategory === 'Beef Meat'
      ? products.filter(p => p.category === 'Beef Meat' || p.category === 'Elite Beef Steaks')
      : products.filter(p => p.category === selectedCategory);

  // Get starting price (lowest price) for a product
  const getStartingPrice = (product) => {
    if (!product.pricing) return 0;
    const prices = Object.values(product.pricing);
    return Math.min(...prices);
  };

  const getCategoryLabel = (cat) => {
    if (cat === 'All') return t('all');
    return getCategoryName(cat);
  };

  return (
    <div className="min-h-screen" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero */}
      <section className="bg-stone-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">{t('ourProducts')}</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('productsSubtitle')}
          </p>
        </div>
      </section>

      {/* Product Categories Filter */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setSelectedCategory(category)}
                variant={selectedCategory === category ? 'default' : 'outline'}
                className={`${selectedCategory === category 
                  ? 'bg-red-600 hover:bg-red-700 text-white' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                } whitespace-nowrap`}
                data-testid={`filter-${category.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {getCategoryLabel(category)}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600">
                {language === 'ar' ? 'لا توجد منتجات في هذه الفئة.' : 'No products found in this category.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product) => (
                <Card key={product.id} className="overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border-0 shadow-lg">
                  <Link to={`/product/${product.id}`}>
                    <div className="relative h-72 overflow-hidden group">
                      <img
                        src={product.image}
                        alt={getProductName(product.name)}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'}`}>
                        <span className="bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          {getCategoryName(product.category)}
                        </span>
                      </div>
                    </div>
                  </Link>
                  <CardContent className="p-6">
                    <Link to={`/product/${product.id}`}>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3 hover:text-red-600 transition-colors">
                        {getProductName(product.name)}
                      </h3>
                    </Link>
                    <p className="text-gray-600 mb-4 leading-relaxed">{getDescription(product.description)}</p>
                    <div className="mb-6">
                      <span className="text-sm text-gray-600">
                        {language === 'ar' ? 'يبدأ من' : 'Starting from'}
                      </span>
                      <div className="text-2xl font-bold text-red-600 mt-1">
                        {formatLocalPrice(getStartingPrice(product))} {t('egp')}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        asChild
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white h-12"
                      >
                        <Link to={`/product/${product.id}`}>
                          <ShoppingCart size={18} className={isRTL ? 'ml-2' : 'mr-2'} />
                          {language === 'ar' ? 'اختر الخيارات' : 'Select Options'}
                        </Link>
                      </Button>
                      <Button 
                        asChild
                        variant="outline" 
                        className="border-red-600 text-red-600 hover:bg-red-50"
                      >
                        <Link to={`/product/${product.id}`}>
                          {language === 'ar' ? 'التفاصيل' : 'Details'}
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Product Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'ar' ? 'مميزات المنتج' : 'Product Features'}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  {language === 'ar' ? 'جودة ممتازة' : 'Premium Quality'}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {language === 'ar' 
                    ? 'يتم اختيار كل منتج ومعالجته بعناية لتلبية أعلى معايير الجودة.'
                    : 'Every product is carefully selected and processed to meet the highest quality standards.'}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  {language === 'ar' ? 'طازج وآمن' : 'Fresh & Safe'}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {language === 'ar'
                    ? 'بروتوكولات سلامة الغذاء الصارمة تضمن النضارة والسلامة من المزرعة إلى التوصيل.'
                    : 'Rigorous food safety protocols ensure freshness and safety from farm to delivery.'}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  {language === 'ar' ? 'تغليف مخصص' : 'Custom Packaging'}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {language === 'ar'
                    ? 'خيارات تغليف مرنة لتلبية الاحتياجات المحددة لتجار الجملة والتجزئة.'
                    : 'Flexible packaging options to meet the specific needs of wholesalers and retailers.'}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            {language === 'ar' ? 'هل تحتاج حلول مخصصة؟' : 'Need Custom Solutions?'}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {language === 'ar'
              ? 'نقدم منتجات مخصصة وحلول تغليف لطلبات الجملة والكميات الكبيرة.'
              : 'We offer customized products and packaging solutions for wholesale and bulk orders.'}
          </p>
          <Button asChild size="lg" className="bg-white text-red-600 hover:bg-gray-100">
            <Link to="/contact">
              {language === 'ar' ? 'تواصل مع فريق المبيعات' : 'Contact Our Sales Team'}
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Products;
